import React from 'react'
import {
  IconButton,
  InputAdornment,
  SvgIcon,
  TextField as MuiTextField,
  Typography
} from '@mui/material'

import { WPCF7Form } from '@/types'

export type TextFieldProps = {
  field: WPCF7Form['form'][0]
  formik: any
  passwordVisibility: Record<string, boolean>
  setPasswordVisibility: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
}

const TextFieldComponent = ({
  field,
  formik,
  passwordVisibility,
  setPasswordVisibility
}: TextFieldProps) => {
  const hasFieldError =
    formik.submitCount > 0 &&
    formik.errors[`input_${field.id}`] !== undefined &&
    formik.touched[`input_${field.id}`] !== undefined
  const helperMessage = hasFieldError ? String(formik.errors[`input_${field.id}`]) : ' '
  const isPhoneField = field.type === 'tel' || field.type === 'phone'
  const isVatNumberField = field.adminLabel === 'vat_number'
  const placeholder = field.placeholder || (isPhoneField ? '39 333 1234567' : '')
  const fieldName = `input_${field.id}`
  const rawFieldValue = formik.values[fieldName]
  const inputValue =
    isPhoneField && typeof rawFieldValue === 'string'
      ? rawFieldValue.replace(/^\+/, '')
      : isVatNumberField && typeof rawFieldValue === 'string'
        ? rawFieldValue.toUpperCase()
        : rawFieldValue

  const inputSlotProps = field.enablePasswordInput
    ? {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="medium"
              onClick={() =>
                setPasswordVisibility((prev) => ({
                  ...prev,
                  [field.id]: !prev[field.id]
                }))
              }
              edge="end"
              disabled={formik.isSubmitting}
            >
              <SvgIcon fontSize="small">
                <use href={passwordVisibility[field.id] ? '#ico-eye' : '#ico-eye-closed'} />
              </SvgIcon>
            </IconButton>
          </InputAdornment>
        )
      }
    : isPhoneField
      ? {
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{ mb: 'auto', mt: 'auto', mr: 0.5, pb: 1, height: '2.3rem' }}
            >
              +
            </InputAdornment>
          )
        }
      : undefined

  const handleChange =
    isPhoneField || isVatNumberField
      ? (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (isPhoneField) {
            const valueWithoutPlus = event.target.value.replace(/\+/g, '')
            formik.setFieldValue(fieldName, valueWithoutPlus ? `+${valueWithoutPlus}` : '')
            return
          }

          formik.setFieldValue(fieldName, event.target.value.toUpperCase())
        }
      : formik.handleChange

  return (
    <>
      {field.label && field.labelPlacement !== 'hidden' && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 1,
            ...(hasFieldError && {
              color: 'error.main'
            })
          }}
        >
          {field.label}
          {field.isRequired ? ' *' : ''}
        </Typography>
      )}
      <MuiTextField
        id={`input_${field.id}`}
        name={`input_${field.id}`}
        type={
          field.enablePasswordInput
            ? passwordVisibility[field.id]
              ? 'text'
              : 'password'
            : field.type
        }
        placeholder={placeholder}
        multiline={field.type === 'textarea'}
        slotProps={{
          htmlInput: { maxLength: field.maxLength || undefined },
          input: inputSlotProps
        }}
        helperText={
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 1, visibility: hasFieldError ? 'visible' : 'hidden' }}
          >
            {helperMessage}
          </Typography>
        }
        error={hasFieldError}
        disabled={formik.isSubmitting}
        fullWidth
        variant="standard"
        value={inputValue}
        onChange={handleChange}
        onBlur={formik.handleBlur}
        sx={(theme) => ({
          '& .MuiInputBase-root': {
            typography: 'textRegular'
          },
          '& .MuiInputBase-root.MuiInputBase-multiline': {
            ...(field.type === 'textarea' && {
              alignItems: 'flex-start',
              borderRadius: theme.shape.borderRadius2,
              border: `1px solid ${theme.palette.divider}`
            })
          },
          '& .MuiInputBase-root.MuiInputBase-multiline:hover': {
            ...(field.type === 'textarea' && {
              borderColor: theme.palette.text.secondary
            })
          },
          '& .MuiInputBase-root.MuiInputBase-multiline.Mui-error': {
            ...(field.type === 'textarea' && {
              border: `1px solid ${theme.palette.error.main}`
            })
          },
          '& .MuiInputBase-input': {
            p: 0,
            pb: 1,
            minHeight: '2.1rem',
            typography: 'textRegular',
            ...(field.type === 'textarea' && {
              boxSizing: 'border-box',
              minHeight: '10.1rem',
              p: 2
            })
          },
          '& .MuiInput-underline:before': {
            borderBottomColor: 'rgba(0, 0, 0, 0.1)',
            ...(field.type === 'textarea' && {
              content: 'none',
              borderBottom: 'none'
            })
          },
          '& .MuiInput-underline:hover:before': {
            borderBottomColor: 'rgba(0, 0, 0, 0.4)',
            ...(field.type === 'textarea' && {
              borderBottom: 'none'
            })
          },
          '& .MuiInput-underline:after': {
            borderBottomColor: 'rgba(0, 0, 0, 0.4)',
            ...(field.type === 'textarea' && {
              content: 'none',
              borderBottom: 'none'
            })
          },
          '& .MuiInput-underline.Mui-error:after': {
            borderBottomColor: field.type === 'textarea' ? 'transparent' : 'error.main',
            ...(field.type === 'textarea' && {
              content: 'none',
              borderBottom: 'none'
            })
          },
          ...(hasFieldError &&
            (field.type === 'textarea'
              ? {}
              : {
                  '& .MuiInput-underline:before': {
                    borderBottomColor: 'error.main'
                  },
                  '& .MuiInput-underline:hover:before': {
                    borderBottomColor: 'error.main'
                  },
                  '& .MuiInput-underline:after': {
                    borderBottomColor: 'error.main'
                  }
                })),
          ...(formik.isSubmitting && {
            pointerEvents: 'none',
            opacity: 0.4
          }),
          '& .MuiInputBase-input::placeholder': {
            color: theme.palette.text.disabled,
            opacity: 0.85
          }
        })}
      />
      {field.description && (
        <Typography
          variant="textSmall"
          sx={{
            mt: 1,
            display: 'block',
            color: 'text.secondary'
          }}
        >
          {field.description}
        </Typography>
      )}
      {field.type === 'textarea' && field.maxLength && (
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: 'block',
            color: 'text.secondary'
          }}
        >
          {(formik.values[`input_${field.id}`] || '').length} / {field.maxLength}
        </Typography>
      )}
    </>
  )
}

export default TextFieldComponent
